import React, { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { slugify, trackEvent } from '@surfline/web-common';
import { useRouter } from 'next/router';

import { UserFavorites } from 'types/userFavorites';

import SpotPreviewCardWithClarity from 'components/SpotPreviewCardWithClarity';
import { Units } from 'types/units';
import { useMaxWidthTablet, useMaxWidthTabletLarge } from 'hooks/useMediaQueries';
import useInfiniteScroll from './useInfinateScroll';

interface Props {
  selectedFavorites: UserFavorites | undefined;
  units: Units;
}
const FavoritesLive: React.FC<Props> = ({ selectedFavorites, units }) => {
  const isMaxWidthTablet = useMaxWidthTablet();
  const isMaxWidthTabletLarge = useMaxWidthTabletLarge();

  // Ensure units has waveHeight property for SpotPreviewCardWithClarity
  const unitsWithWaveHeight = useMemo(() => {
    if (units.waveHeight) {
      return units;
    }
    return { ...units, waveHeight: (units as any).surfHeight || 'ft' };
  }, [units]);

  const calculateGridRepeat = () => {
    if (isMaxWidthTablet) {
      return '1';
    }
    if (isMaxWidthTabletLarge) {
      return '2';
    }
    return '3';
  };

  const { numberToRender, lastElementRef } = useInfiniteScroll(selectedFavorites);

  const router = useRouter();

  const onClickHandler = useCallback(
    (title: string, cardIndex: number, id: string) => {
      trackEvent('Carousel Clicked', {
        carouselItem: title,
        carouselName: 'Favorites Modal',
        category: 'kbyg',
        path: router.asPath,
        positionNumber: cardIndex,
        title: 'Favorites',
      });
      window.location.href = `/surf-report/${slugify(title)}/${id}`;
    },
    [router.asPath],
  );
  return (
    <Box data-testid="favorites-live-fullscreen">
      {selectedFavorites && (
        <Box display="grid" gridTemplateColumns={`repeat(${calculateGridRepeat()}, 1fr)`} gap={2}>
          {selectedFavorites.slice(0, numberToRender).map((fav, index) => (
            <Box key={fav.name}>
              <div ref={index + 1 === numberToRender ? lastElementRef : undefined}>
                <SpotPreviewCardWithClarity
                  cardIndex={index}
                  onClickHandler={() => onClickHandler(fav.name, index, fav._id)}
                  spotReport={fav}
                  units={unitsWithWaveHeight}
                  layout={isMaxWidthTablet ? 'COMPACT' : 'FULL'}
                  href={`/surf-report/${slugify(fav.name)}/${fav._id}`}
                  clipPathIds={{
                    surfBarGraphIcon: `surf-bar-graph-icon-${index}`,
                    windBarGraphIcon: `wind-bar-graph-icon-${index}`,
                  }}
                />
              </div>
            </Box>
          ))}
          {numberToRender === 0 && <div ref={lastElementRef} />}
        </Box>
      )}
    </Box>
  );
};

export default FavoritesLive;
